import { useId, useMemo, useState } from 'react'
import { CheckOutlined, IconLink, SearchOutlined } from '../../../components/icons'
import { Button } from '../../../components/ui/Button'
import { Modal } from '../../../components/ui/Modal'
import { useToast } from '../../../components/ui/ToastProvider'

export interface PartShareModalProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly partId: string
  readonly partName: string
}

interface SharePerson {
  readonly id: string
  readonly initials: string
  readonly name: string
  readonly role: string
  readonly bg: string
}

/**
 * UID-422 — the design's "Share / add members" modal (`Fictiv
 * Platform.dc.html` ~L4823-4854) sources its people list from a hardcoded
 * mock roster (`wsRoster()`/`peopleDirectory()`, ~L7335-7349) — there is no
 * real org-members endpoint yet, and product's explicit direction for this
 * ticket is to hardcode this list from the design rather than wire one up.
 * Names/roles/avatar tints are copied verbatim; only the "Copy link" action
 * (below) is real.
 */
const SHARE_DIRECTORY: readonly SharePerson[] = [
  { id: 'JL', initials: 'JL', name: 'Jordan Lee', role: 'Mechanical engineer', bg: '#0F208B' },
  { id: 'SC', initials: 'SC', name: 'Sam Carter', role: 'Design engineer', bg: '#4B7FFC' },
  { id: 'MR', initials: 'MR', name: 'Maria Ruiz', role: 'Systems engineer', bg: '#8b5cf6' },
  { id: 'DP', initials: 'DP', name: 'Devin Park', role: 'Manufacturing engineer', bg: '#F0811E' },
  { id: 'AN', initials: 'AN', name: 'Aisha Nakamura', role: 'Program lead', bg: '#F54A4A' },
]

/**
 * UID-422 — "Share part" modal (design: the generic share/add-members
 * overlay, `kind:'part'`). Callers mount this conditionally (`{sharePart &&
 * <PartShareModal .../>}`, matching this file's sibling modals' convention)
 * so every open starts with fresh search/added/copied state — there is no
 * reset-on-close effect here because the component itself unmounts on close.
 *
 * Only "Copy link" is a real feature (copies the part's own detail-page URL
 * to the clipboard); the search box, the people directory, and the "+ Add"/
 * "Added" toggle are the design's own hardcoded mock data/logic — see
 * `SHARE_DIRECTORY`'s doc comment above. "Done" always closes the modal,
 * confirming via toast how many people were toggled on, exactly mirroring
 * the design's own `shareInvite()` copy.
 */
export function PartShareModal({ open, onClose, partId, partName }: PartShareModalProps) {
  const { showToast } = useToast()
  const searchFieldId = useId()
  const [query, setQuery] = useState('')
  const [added, setAdded] = useState<ReadonlySet<string>>(() => new Set())
  const [copied, setCopied] = useState(false)

  const people = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    if (!normalized) return SHARE_DIRECTORY
    return SHARE_DIRECTORY.filter(
      (person) =>
        person.name.toLowerCase().includes(normalized) ||
        person.role.toLowerCase().includes(normalized),
    )
  }, [query])

  function toggleAdded(id: string) {
    setAdded((current) => {
      const next = new Set(current)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  async function handleCopyLink() {
    const url = `${window.location.origin}/parts/${partId}`
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      showToast('Link copied to clipboard', 'success')
    } catch {
      showToast('Couldn’t copy the link. Try again.', 'error')
    }
  }

  function handleDone() {
    const addedCount = added.size
    onClose()
    if (addedCount > 0) {
      showToast(`${addedCount} person${addedCount > 1 ? 's' : ''} shared with`, 'success')
    }
  }

  const doneLabel = added.size > 0 ? `Add ${added.size} person${added.size > 1 ? 's' : ''}` : 'Done'

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Share · ${partName}`}
      subtitle="Share this part with a teammate, or copy a link."
      footer={
        <>
          <Button variant="outline" size="lg" onClick={() => void handleCopyLink()}>
            <IconLink size={16} />
            {copied ? 'Link copied' : 'Copy link'}
          </Button>
          <span className="part-share-modal-footer-spacer" />
          <button type="button" className="btn btn-lg part-share-modal-done" onClick={handleDone}>
            {doneLabel}
          </button>
        </>
      }
    >
      <div className="part-share-modal-search-wrap">
        <span className="part-share-modal-search-icon" aria-hidden="true">
          <SearchOutlined />
        </span>
        <label className="sr-only" htmlFor={searchFieldId}>
          Search people
        </label>
        <input
          id={searchFieldId}
          type="search"
          className="part-share-modal-search-input"
          placeholder="Search people by name or role…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      <ul className="part-share-modal-people">
        {people.map((person) => {
          const isAdded = added.has(person.id)
          return (
            <li key={person.id} className="part-share-modal-person">
              <span
                className="part-share-modal-avatar"
                style={{ backgroundColor: person.bg }}
                aria-hidden="true"
              >
                {person.initials}
              </span>
              <span className="part-share-modal-person-text">
                <span className="part-share-modal-person-name">{person.name}</span>
                <span className="part-share-modal-person-role">{person.role}</span>
              </span>
              {isAdded ? (
                <button
                  type="button"
                  className="part-share-modal-added-btn"
                  onClick={() => toggleAdded(person.id)}
                >
                  <CheckOutlined aria-hidden="true" />
                  Added
                </button>
              ) : (
                <button
                  type="button"
                  className="part-share-modal-add-btn"
                  onClick={() => toggleAdded(person.id)}
                >
                  + Add
                </button>
              )}
            </li>
          )
        })}
      </ul>
    </Modal>
  )
}
